import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useAuthState } from '~/auth';
import {
  Horse,
  HorseChangeColorRequest,
  HorseChangeColorResponse,
  HorseChangeJockeyNameRequest,
  HorseChangeJockeyNameResponse,
  HorseChangeNameRequest,
  HorseChangeNameResponse,
} from '~/server/src/dtos';

import { getFn, putFn, queryClient } from './api';
import { account } from './apiAccount';
import { useFirebaseAnalytics } from './useFirebase';

export const horses = createQueryKeys('horses', {
  detail: (horseId: string) => ({ queryKey: [horseId] }),
  all: null,
});

export const getHorse = (horseId: string): string => `/horses/${horseId}`;
export const putHorseChangeName = (horseId: string): string => `${getHorse(horseId)}/name`;
export const putHorseChangeColor = (horseId: string): string => `${getHorse(horseId)}/color`;
export const putHorseChangeJockeyName = (horseId: string): string =>
  `${getHorse(horseId)}/jockeyName`;

export const useHorse = (
  horseId = '',
  options?: Omit<Parameters<typeof useQuery<Horse>>[0], 'queryKey' | 'queryFn'>,
) => {
  const { isAuthorized, state: authState } = useAuthState();

  return useQuery<Horse>({
    ...horses.detail(horseId),
    queryFn: ({ signal }) => getFn(getHorse(horseId), undefined, undefined, { signal }),
    ...options,
    enabled:
      authState !== 'initializing' && isAuthorized && !!horseId && (options?.enabled ?? true),
  });
};

export type HorseRequest<T> = { horseId: string } & T;

export const useChangeHorseName = (
  options?: Parameters<
    typeof useMutation<HorseChangeNameResponse, unknown, HorseRequest<HorseChangeNameRequest>>
  >[0],
) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<HorseChangeNameResponse, unknown, HorseRequest<HorseChangeNameRequest>>({
    mutationFn: ({ horseId, ...data }) => putFn(putHorseChangeName(horseId), data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ queryKey: horses.all.queryKey });
      queryClient.invalidateQueries({ queryKey: account._def });
      options?.onSuccess?.(...response);
      analytics?.logEvent('update_horse_name');
    },
  });
};

export const useChangeHorseColor = (
  options?: Parameters<
    typeof useMutation<HorseChangeColorResponse, unknown, HorseRequest<HorseChangeColorRequest>>
  >[0],
) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<HorseChangeColorResponse, unknown, HorseRequest<HorseChangeColorRequest>>({
    mutationFn: ({ horseId, ...data }) => putFn(putHorseChangeColor(horseId), data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ queryKey: horses.all.queryKey });
      queryClient.invalidateQueries({ queryKey: account._def });
      options?.onSuccess?.(...response);
      analytics?.logEvent('update_horse_color', { color: response[0].color });
    },
  });
};

export const useChangeHorseJockeyName = (
  options?: Parameters<
    typeof useMutation<
      HorseChangeJockeyNameResponse,
      unknown,
      HorseRequest<HorseChangeJockeyNameRequest>
    >
  >[0],
) => {
  const analytics = useFirebaseAnalytics();
  return useMutation<
    HorseChangeJockeyNameResponse,
    unknown,
    HorseRequest<HorseChangeJockeyNameRequest>
  >({
    mutationFn: ({ horseId, ...data }) => putFn(putHorseChangeJockeyName(horseId), data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ queryKey: horses.all.queryKey });
      queryClient.invalidateQueries({ queryKey: account._def });
      options?.onSuccess?.(...response);
      analytics?.logEvent('update_horse_jockeyname');
    },
  });
};
